import { gql, useMutation, useQuery } from '@apollo/client'

import { useLinkingAnonymousAccountState } from '@/clients/utoniq-core'
import {
  CollectionFeeDetailFragmentDoc,
  FeeDetailFragmentDoc,
  PaymentDetailFragmentDoc,
  PurchasablePrepaidPointConnectionFragmentDoc,
  SendVerificationSms,
  SendVerificationSmsMutation,
  SendVerificationSmsMutationVariables,
  StoreItemPurchaseProceedQuery,
  StoreItemPurchaseProceedQueryVariables,
  UserAddressFragmentDoc,
  UserCartStoreItemPurchaseProceedFragment,
  UserCartStoreItemPurchaseProceedFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { usePointPurchase } from '@/components/pages/PrepaidPointCharge/viewModel'
import { useCurrentUser, useMessage } from '@/core'
import { makeViewModel } from '@/core/architecture'
import {
  addCollectionToCollectionFee,
  groupItemsByCollection,
} from '@/core/cart'
import { getPlatform } from '@/core/native/app'
import { useRouter } from '@/core/router'
import { convertUserCartItemsToSku } from '@/core/sku'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const linkingAnonymousAccount = useLinkingAnonymousAccountState()

  const { router, routerPages } = useRouter<{
    cartId: string
  }>()
  const { cartId } = router.query
  const { showError } = useMessage()
  const platform = getPlatform()
  const { purchaseByIosAndAndroid, purchaseByWeb, chargeLoading } =
    usePointPurchase(user?.uid, platform, 'artistStoreItemDetail')

  const { data, loading } = useQuery<
    StoreItemPurchaseProceedQuery,
    StoreItemPurchaseProceedQueryVariables
  >(QUERY, {
    variables: {
      cartId,
      purchasablePrepaidPointPageInput: { limit: 30 },
      platform,
      isKonbini: true,
    },
    // waiting for linking anonymous account
    skip: linkingAnonymousAccount,
  })

  const [sendVerificationSMS, { loading: sendingVerification }] = useMutation<
    SendVerificationSmsMutation,
    SendVerificationSmsMutationVariables
  >(SendVerificationSms)

  const cartItems = convertUserCartItemsToSku(
    data?.userCart?.items ?? null
  ) as UserCartStoreItemPurchaseProceedFragment['items']

  // カートのアイテムをコレクションごとにグルーピングしたもの
  const cartItemsByCollection = groupItemsByCollection(cartItems)

  // 購入済みイベントコレクションアイテムの受取時間と個数
  const myPickupUsers = extractMyPickupUsers(cartItemsByCollection)

  // カート内容のアイテムが購入制限を超過しているか
  const exceededItemCount = cartItems?.some(cartItem => {
    // 注文ごとの上限とカート内容を比較
    const exceedPurchaseLimitPerOrder =
      !!cartItem.item.purchaseLimitPerOrder &&
      cartItem.item.purchaseLimitPerOrder < cartItem.count
    // アイテム単位の上限とユーザーの所持数+カート内容を比較
    const exceedPurchaseLimitByUser =
      !!cartItem.item.purchaseLimitByUser &&
      cartItem.item.purchaseLimitByUser - (cartItem.item.ownedCount || 0) <
        cartItem.count

    return exceedPurchaseLimitPerOrder || exceedPurchaseLimitByUser
  })

  return {
    state: {
      loading: loading || linkingAnonymousAccount,
      cart: data?.userCart,
      cartItems,
      cartItemsByCollection,
      fullName: data?.user?.fullName || '',
      paymentSource: data?.paymentSource,
      address: data?.user?.address ?? null,
      phoneNumber: data?.user?.phoneNumber ?? '',
      isPhoneNumberVerified: !!data?.user?.isPhoneNumberVerified,
      prepaidPointBalance: data?.prepaidPointBalance ?? null,
      purchasablePrepaidPoints: data?.purchasablePrepaidPoints ?? null,
      pointChargeLoading: chargeLoading,
      isCardRegistered: !!data?.paymentSource?.last4,
      systemFee: addCollectionToCollectionFee(
        data?.userCart?.checkoutFees?.collections,
        data?.userCart?.items.map(({ item }) => item.collection)
      ),
      shippingFee: data?.userCart?.checkoutFees?.shipping ?? null,
      konbiniFee: data?.userCart?.checkoutFees?.konbini ?? null,
      sendingVerification,
      myPickupUsers,
      exceededItemCount,
      /** アプリ毎に設定されているポイント購入のキャリア決済が有効かどうか */
      enablePointCarrierPayment:
        !!data?.organization?.modules?.pointCarrierPayment,
    },
    actions: {
      // ポイントのチャージ
      onPurchasePoint:
        platform === 'web' ? purchaseByWeb : purchaseByIosAndAndroid,
      // 電番認証SMS送信
      async onSendVerificationSMS() {
        sendVerificationSMS()
          .then(() => {
            router.push(routerPages.SMSVerification)
          })
          .catch(e => {
            showError(e)
          })
      },
    },
  }
})

const QUERY = gql`
  query StoreItemPurchaseProceed(
    $cartId: ID!
    $platform: String!
    $purchasablePrepaidPointPageInput: PageInput
    $isKonbini: Boolean
  ) {
    userCart(id: $cartId) {
      ...UserCartStoreItemPurchaseProceed
      checkoutFees(isKonbini: $isKonbini) {
        totalFee
        shipping {
          ...FeeDetail
        }
        collections {
          ...CollectionFeeDetail
        }
        konbini {
          ...FeeDetail
        }
      }
    }
    paymentSource {
      ...PaymentDetail
    }
    user {
      id
      fullName
      phoneNumber
      formattedPhoneNumber @client
      isPhoneNumberVerified
      ...UserAddress
    }
    prepaidPointBalance {
      lastUpdatedAt
      prepaidPointId
      balance
      formattedPrepaidPointBarance @client
    }
    purchasablePrepaidPoints(
      platform: $platform
      pageInput: $purchasablePrepaidPointPageInput
    ) {
      ...PurchasablePrepaidPointConnection
    }
    organization {
      id
      modules {
        pointCarrierPayment
      }
    }
  }
  ${PurchasablePrepaidPointConnectionFragmentDoc}
  ${PaymentDetailFragmentDoc}
  ${UserAddressFragmentDoc}
  ${UserCartStoreItemPurchaseProceedFragmentDoc}
  ${FeeDetailFragmentDoc}
  ${CollectionFeeDetailFragmentDoc}
`

/**
 * カート内容から購入済みイベントコレクションアイテムの受取時間と個数を返す
 * @param cartItemsByCollection カート内容をコレクションごとにグルーピングしたもの
 */
const extractMyPickupUsers = (
  cartItemsByCollection: ReturnType<typeof groupItemsByCollection>
) => {
  if (!cartItemsByCollection) return []

  // コレクションごとに購入済みアイテムの受取時間と個数を抽出
  const myPickupUsers = cartItemsByCollection
    .flatMap(({ collection }) =>
      collection?.event?.eventCollections.edges.flatMap(({ node }) =>
        node.myPickupUsers?.map(myPickupUser =>
          myPickupUser.cartTransactionOrders.flatMap(order =>
            order.items.flatMap(item => ({
              itemId: item.itemId,
              count: item.count,
              pickupTime: myPickupUser.pickupTime,
              collectionId: node.collectionId,
            }))
          )
        )
      )
    )
    .filter(Boolean)

  if (!myPickupUsers.length) return []

  // pickupTime単位でまとめるためのオブジェクトを用意

  const groupedByPickupTime: Record<
    number,
    Record<string, { count: number; collectionId: string }>
  > = {}

  // pickupTimeをキーに { itemId: count } の集計を行う
  myPickupUsers.flat().forEach(item => {
    const pickupTime = item?.pickupTime
    const itemId = item?.itemId || ''
    const collectionId = item?.collectionId || ''

    if (!groupedByPickupTime[pickupTime]) {
      groupedByPickupTime[pickupTime] = {}
    }

    if (!groupedByPickupTime[pickupTime][itemId]) {
      groupedByPickupTime[pickupTime][itemId] = { count: 0, collectionId }
    }

    groupedByPickupTime[pickupTime][itemId].count += item?.count || 0
  })

  // オブジェクトの整型
  const result = Object.keys(groupedByPickupTime).map(pickupTime => {
    const items = Object.keys(groupedByPickupTime[parseInt(pickupTime)]).map(
      itemId => {
        const itemInfo = groupedByPickupTime[parseInt(pickupTime)][itemId]
        return {
          itemId: itemId,
          count: itemInfo.count,
          collectionId: itemInfo.collectionId,
        }
      }
    )

    return {
      pickupTime: parseInt(pickupTime),
      items: items,
    }
  })

  return result
}
